import React, { useContext } from 'react'
import AppInfoContext from 'src/provider/state-manager/appInfoProvider'
import VisibilityContext from 'src/provider/state-manager/visibilityProvider'
import { authApiCaller, productApiCaller, adminApiCaller, billingApiCaller } from '../interceptor'
import utility, { catchErrMsg, erroMessage, loglite, requestMessage } from 'src/utils/utility'
import { AuthModel, BillingConfig, DropdownOption, EmailShare, Fee, PaginationInfo, Product, ProductRequest, ProductReview, ReportResponse, ServiceCategory, ServiceFee, ServiceProvider, User, UserType } from 'src/model'
import { useNotifier } from "react-pop-notifier"
import { appSettings } from 'src/provider/config/constant'
import helpers from 'src/utils/helpers'
import { sampleSearchedData } from 'src/utils/mock-data'

const ApiContext = React.createContext<any>(null)

export const ApiProvider = (props: any) => {
    const {info, setInfoProperty} = useContext(AppInfoContext)
    const {loader} = useContext(VisibilityContext)
    const {notifier} = useNotifier()

    async function loginAsAdmin (payload: AuthModel) {
        try {
            loader(true)
            const { data } = await authApiCaller.post(`Authentication/Login`, payload)
            loader(false)

            loglite({data}, 'loginAsAdmin API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                const info = {...data.data}

                await Promise.all([
                    setInfoProperty('authToken', info.accessToken),
                    setInfoProperty('userData', info),
                    setInfoProperty('userType', payload.userType)
                ])

                return info
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
            // await Promise.all([
            //     setInfoProperty('authToken', 'ahdgdg'),
            //     setInfoProperty('userType', payload.userType)
            // ])
            // return true
        }
    }

    async function institutionLoginWithToken (payload: AuthModel) {
        try {
            loader(true)
            const { data } = await authApiCaller.post(`Authentication/InstitutionLoginWithToken`, payload)
            loader(false)

            loglite({data}, 'institutionLoginWithToken API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                const info = {...data.data, institutionCode: payload.institutionCode}

                await Promise.all([
                    setInfoProperty('authToken', info.accessToken),
                    setInfoProperty('userData', info),
                    setInfoProperty('userType', payload.userType),
                ])

                return info
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function validateSSOToken (token: string) {
        try {
            loader(true)
            const { data } = await authApiCaller.get(`Authentication/ValidateToken`, {
                params: { token }
            })
            loader(false)

            loglite({data}, 'validateSSOToken API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                const info = {...data.data}
                await Promise.all([
                    setInfoProperty('authToken', info.accessToken),
                    setInfoProperty('userData', {...info.userDetails, institution: info.institution}),
                    setInfoProperty('userType', info.userDetails?.userType || UserType.CLIENT),
                    setInfoProperty('sso_token', token)
                ])

                return info
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getProducts (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`Product/paged`, payload)
            loader(false)

            loglite({data}, 'getProducts API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getProductsWithoutPagination (payload: any) {
        try {
            loader(true)
            const {data} = await productApiCaller.get(`Product`, {
                params: {...payload}
            })
            loader(false)

            loglite({data}, 'getProductsWithoutPagination API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getProductById (id: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.get(`Product/${id}`)
            loader(false)

            loglite({data}, 'getProductById API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('selectedProduct', data.data)
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function createProduct (payload: Product) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`Product`, payload)
            loader(false)

            loglite({data}, 'createProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Product Successfully Created', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function updateProduct (payload: Product, id: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.put(`Product/${id}`, payload)
            loader(false)

            loglite({data}, 'updateProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('selectedProduct', data.data)
                notifier.show(data.message || 'Product Successfully Updated', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function deleteProduct (id: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.delete(`Product/Delete/${id}`)
            loader(false)

            loglite({data}, 'deleteProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Product Successfully Deleted', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function activateProduct (id: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`Product/Enable/${id}`)
            loader(false)

            loglite({data}, 'activateProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Product Successfully Activated', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function deactivateProduct (id: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`/Product/Disable${id}`)
            loader(false)

            loglite({data}, 'deactivateProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Product Successfully Deactivated', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }


async function activateOrDeactivateCategory(id: string, categoryStatus: string) {
    try {
        loader(true)
        
        const { data } = await productApiCaller.put(`/InstitutionProduct/${categoryStatus}/${id}?institutionCode=`)
        
        loader(false)

        loglite({ data }, `${categoryStatus} API`)

        if (!data.isSuccessful) {
            notifier.show(requestMessage(data))
            return false
        } else {
            const action = categoryStatus === 'ActivateCategory' ? 'Activated' : 'Deactivated'
            notifier.show(data.message || `Product Successfully ${action}`, null, 'success')
            return true
        }
    } catch (err) {
        loader(false)
        notifier.show(erroMessage(catchErrMsg(err)))
        return null
    }
}


    async function publishProduct(id: string, option: boolean) {
        try {
            loader(true);
            const { data } = await productApiCaller.put(`Product/Publish/${id}/${option}`);
            loader(false);
            
            loglite({ data }, `${option ? 'publish' : 'unpublish'}Product API`);
            
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data));
                return false;
            }

            const successMessage = data.message || `Product Successfully ${option ? 'Published' : 'Unpublished'}`

            notifier.show(successMessage, null, 'success');
            return true;
        }
        catch (err) {
            loader(false);
            notifier.show(erroMessage(catchErrMsg(err)));
            return null;
        }
    }


    async function shareToEmail(payload: EmailShare,) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`Product/ShareToEmail`, payload)
            loader(false)

            loglite({data}, 'shareEmail API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Product Shared to Email Successfully', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }


    async function shareToInstitution(id: string, institutionCode: string) {
        try {
            loader(true)

            const {data} = await productApiCaller.post(`Product/ShareToInstitutionUsers/${id}?institutionCode=${institutionCode}`)
            loader(false)

            loglite({data}, 'shareToInstitutionUsers API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Product Shared to Institution Successfully', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }


    async function createUser (payload: User) {
        try {
            loader(true)
            const {data} = await authApiCaller.post(`User/CreateUser`, payload)
            loader(false)

            loglite({data}, 'createUser API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'User Successfully Created', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }
            
    async function getUserByUserType (requestBody: any) {
        try {
            loader(true)

            const { data } = await authApiCaller.post(`User/paged`, requestBody)
            loader(false)

            loglite({data}, 'getUserByUserType API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('selectedUser', data.data)
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getAllSSOUsers (id: string) {
        try {
            loader(true)
            const { data } = await authApiCaller.get(`User/sso/users/${id}`)
            loader(false)
            loglite({data}, 'getAllSSOUsers API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getRoleByUserType (userType: string) {
        try {
            loader(true)
            const {data} = await authApiCaller.get(`Role?UserType=${userType}`)
            loader(false)

            loglite({data}, 'getRoleByUserType API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('selectedRole', data.data)
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getFees (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await billingApiCaller.post(`Fee/paged`, payload)
            loader(false)

            loglite({data}, 'getFees API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getFeesWithoutPagination () {
        try {
            loader(true)
            const {data} = await billingApiCaller.get(`Fee`)
            loader(false)

            loglite({data}, 'getFeesWithoutPagination API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getFeesForDownload () {
        try {
            loader(true)
            const {data} = await billingApiCaller.get(`Fee`)
            loader(false)

            loglite({data}, 'getFeesForDownload API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function CreateFee (payload: Fee) {
        try {
            loader(true)
            const {data} = await billingApiCaller.post(`Fee`, payload)
            loader(false)

            loglite({data}, 'CreateFee API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Fee Successfully Created', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function UpdateFee (feeId: number, payload: Fee) {
        try {
            loader(true)
            const {data} = await billingApiCaller.put(`Fee/${feeId}`, payload)
            loader(false)

            loglite({data}, 'UpdateFee API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Fee Successfully Updated', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getSettingsCategories (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`InstitutionProduct/ProductCategories`, payload)
            loader(false)

            loglite({data}, 'getSettingsCategories API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getServiceCategories (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`ServiceCategory/paged`, payload)
            loader(false)

            loglite({data}, 'getServiceCategories API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getServiceCategoriesForDownload () {
        try {
            loader(true)
            const {data} = await adminApiCaller.get(`ServiceCategory`)
            loader(false)

            loglite({data}, 'getServiceCategoriesForDownload API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getServiceCategoriesWithoutPagination () {
        try {
            loader(true)
            const {data} = await adminApiCaller.get(`ServiceCategory`)
            loader(false)

            loglite({data}, 'getServiceCategoriesWithoutPagination API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function CreateServiceCategory (payload: ServiceCategory) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`ServiceCategory`, payload)
            loader(false)

            loglite({data}, 'CreateServiceCategory API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Service Category Successfully Created', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function UpdateServiceCategory (categoryId: number, payload: ServiceCategory) {
        try {
            loader(true)

            const {data} = await adminApiCaller.put(`ServiceCategory/${categoryId}`, payload)
            loader(false)

            loglite({data}, 'UpdateServiceCategory API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Service Category Successfully Updated', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getServiceProviders (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`ServiceProvider/paged`, payload)
            loader(false)

            loglite({data}, 'getServiceProviders API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getServiceProviderById (id: string) {
        try {
            loader(true)
            const {data} = await adminApiCaller.get(`ServiceProvider/${id}`)
            loader(false)

            loglite({data}, 'getServiceProviderById API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('selectedServiceProvider', data.data)
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function CreateServiceProvider (payload: ServiceProvider) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`ServiceProvider`, payload)
            loader(false)

            loglite({data}, 'CreateServiceProvider API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Service Provider Successfully Created', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function UpdateServiceProvider (payload: ServiceProvider) {
        try {
            loader(true)
            const {data} = await adminApiCaller.put(`ServiceProvider/${payload.id}`, payload)
            loader(false)

            loglite({data}, 'UpdateServiceProvider API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Service Provider Successfully Updated', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function activateServiceProvider (id: string) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`ServiceProvider/Enable/${id}`)
            loader(false)

            loglite({data}, 'activateServiceProvider API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Service Provider Successfully Activated', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function deactivateServiceProvider (id: string) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`ServiceProvider/Disable/${id}`)
            loader(false)

            loglite({data}, 'deactivateServiceProvider API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Service Provider Successfully Deactivated', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getServices (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`Service/paged`, payload)
            loader(false)

            loglite({data}, 'getServices API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getServiceFee (productId: string, serviceId: string) {
        try {
            loader(true)
            const {data} = await billingApiCaller.get(`ServiceFee/${productId}/${serviceId}`)
            loader(false)

            loglite({data}, 'getServiceFee API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function AddFeeToService (payload: ServiceFee) {
        try {
            loader(true)
            const {data} = await billingApiCaller.post(`ServiceFee`, payload)
            loader(false)

            loglite({data}, 'AddFeeToService API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Fee Successfully Assigned to Service', null, 'success')
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function UpdateServiceFee (payload: ServiceFee) {
        try {
            loader(true)
            const {data} = await billingApiCaller.put(`ServiceFee/${payload.id}`, payload)
            loader(false)

            loglite({data}, 'UpdateServiceFee API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Fee Successfully Assigned to Service', null, 'success')
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getOutboundApiCalls (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`OutboundApiLog/paged`, payload)
            loader(false)

            loglite({data}, 'getOutboundApiCalls API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getInstitutionApps (payload: any) {
        try {
            loader(true)
            const {data} = await productApiCaller.get(`InstitutionProduct`, {
                params: { ...payload }
            })
            loader(false)

            loglite({data}, 'getInstitutionApps API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getAllInstitutions () {
        try {
            loader(true)
            const {data} = await authApiCaller.get(`Institution/sso/institutions`)
            loader(false)

            loglite({data}, 'getAllInstitutions API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getBillingConfiguration () {
        try {
            loader(true)
            const {data} = await billingApiCaller.get(`InstitutionBillingConfiguration`, {
                params: { institutionCode: info.userData?.institution?.institutionCode }
            })
            loader(false)

            loglite({data}, 'getBillingConfiguration API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('billingConfig', data.data)
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getBillings (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await billingApiCaller.post(`Billing/paged`, payload)
            loader(false)

            loglite({data}, 'getBillings API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function KeepSessionAlive () {
        try {
            loader(true)
            const {data} = await authApiCaller.get(`Authentication/poll`)
            loader(false)

            loglite({data}, 'KeepSessionAlive API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await setInfoProperty('sessionAliveTrigger', !info.sessionAliveTrigger)
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function configureBilling (payload: BillingConfig) {
        try {
            loader(true)
            const {data} = await billingApiCaller.post(`InstitutionBillingConfiguration`, payload)
            loader(false)

            loglite({data}, 'configureBilling API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Configuration Created Successfully', null, 'success')
                return true
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function updateConfiguredBilling (payload: BillingConfig) {
        try {
            loader(true)
            const {data} = await billingApiCaller.put(`InstitutionBillingConfiguration/${payload.id}`, payload)
            loader(false)

            loglite({data}, 'updateConfiguredBilling API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Configuration Updated Successfully', null, 'success')
                return true
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function activateInstitutionProduct (productId: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.put(`InstitutionProduct/ActivateProduct/${productId}`, {
                params: { institutionCode: info.institutionCode }
            })
            loader(false)

            loglite({data}, 'activateInstitutionProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Institution Product Activated Successfully', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function deactivateInstitutionProduct (productId: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.put(`InstitutionProduct/DeactivateProduct/${productId}`, {
                params: { institutionCode: info.institutionCode }
            })
            loader(false)

            loglite({data}, 'deactivateInstitutionProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Institution Product Deactivated Successfully', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function createProductReview (payload: ProductReview) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`Review/CreateReview`, payload)
            loader(false)

            loglite({data}, 'createProductReview API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Review Submitted Successfully', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getProductReviews (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`Review/paged`, payload)
            loader(false)

            loglite({data}, 'getProductReviews API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function requestForProduct (payload: ProductRequest) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`InstitutionProduct/RequestProductInstallation`, payload)
            loader(false)

            loglite({data}, 'requestForProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Request Successfully Sent', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function installProduct (productId: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`InstitutionProduct/InstallProduct`, {productId})
            loader(false)

            loglite({data}, 'installProduct API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Request Successfully Sent', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function downloadReport (report: ReportResponse, reportName: string) {
        const columns = utility.reportColumns(reportName)
        let data = []
        
        if (columns.length) {
            for (let item of report.data) {
                const rowData: any = {}
                for (let column of columns) {
                    rowData[column] = item[column]
                }
                data.push(rowData)
            }
        }
        else data = report.data || report
        
        utility.downloadReportCSV(data, reportName)

        // if (report.pageCount > 1) notifier.show(downloadLimitMsg, null, 'success')
    }

    async function getSearchedData (text: string) {
        await helpers.wait(3000)
        let filteredData = sampleSearchedData.filter((item: DropdownOption) => item.label?.includes(text) || item.value?.includes(text))
        
        if (filteredData.length === 0) filteredData = sampleSearchedData
        loglite({filteredData}, 'getSearchedData API')

        return filteredData
    }

    async function getPublicProducts (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`public/products/paged`, payload)
            loader(false)

            loglite({data}, 'getPublicProducts API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getPublicServiceCategories (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await adminApiCaller.post(`public/servicecategories/paged`, payload)
            loader(false)

            loglite({data}, 'getPublicServiceCategories API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getPublicProductById (id: string) {
        try {
            loader(true)
            const {data} = await productApiCaller.get(`public/products/${id}`)
            loader(false)

            loglite({data}, 'getPublicProductById API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('selectedProduct', data.data)
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getPublicProductsWithoutPagination (payload: any) {
        try {
            loader(true)
            const {data} = await productApiCaller.get(`public/products`, {
                params: {...payload}
            })
            loader(false)

            loglite({data}, 'getPublicProductsWithoutPagination API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getPublicProductReviews (payload: PaginationInfo) {
        try {
            loader(true)
            const {data} = await productApiCaller.post(`public/reviews/paged`, payload)
            loader(false)

            loglite({data}, 'getPublicProductReviews API')

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function runBackgroundCalls (type: string | Array<string>, waitTime = appSettings.BACKGROUND_JOB_WAIT_TIME) {
        try {
            if (typeof type === 'string') type = [type]

            for (let service of type) {
                await helpers.wait(waitTime)
                console.log({currentBackgroundService: service})
                if (service === 'keep-session-alive') await KeepSessionAlive()
            }
        }
        catch (err) {
            console.log({backgroundService: err})
        }
    }

    const callActions = {
        loginAsAdmin,
        institutionLoginWithToken,
        validateSSOToken,
        getProducts,
        getProductsWithoutPagination,
        createProduct,
        updateProduct,
        deleteProduct,
        activateProduct,
        deactivateProduct,
        activateOrDeactivateCategory,
        publishProduct,
        shareToEmail,
        shareToInstitution,
        createUser,
        getUserByUserType,
        getAllSSOUsers,
        getRoleByUserType,
        getFees,
        getFeesWithoutPagination,
        getFeesForDownload,
        CreateFee,
        UpdateFee,
        getSettingsCategories,
        getServiceCategories,
        getServiceCategoriesForDownload,
        getServiceCategoriesWithoutPagination,
        CreateServiceCategory,
        UpdateServiceCategory,
        getServiceProviders,
        CreateServiceProvider,
        UpdateServiceProvider,
        activateServiceProvider,
        deactivateServiceProvider,
        getServices,
        getServiceFee,
        AddFeeToService,
        UpdateServiceFee,
        getOutboundApiCalls,
        getInstitutionApps,
        getAllInstitutions,
        getBillingConfiguration,
        getBillings,
        KeepSessionAlive,
        runBackgroundCalls,
        configureBilling,
        updateConfiguredBilling,
        activateInstitutionProduct,
        deactivateInstitutionProduct,
        createProductReview,
        getProductReviews,
        getProductById,
        getServiceProviderById,
        requestForProduct,
        installProduct,
        getSearchedData,
        getPublicProducts,
        getPublicServiceCategories,
        getPublicProductById,
        getPublicProductsWithoutPagination,
        getPublicProductReviews
    }

    return (
        <ApiContext.Provider value={{API: callActions, downloadReport}} >
            {props.children}
        </ApiContext.Provider>
    )
}

export default ApiContext